import moment from 'moment-timezone';
import TicketType from './TicketType';

class Occurrence {
    cancelled: boolean = false; 
    creditdate: string = '';
    date_friendly: string = '';
    end_date: string = '';
    event_max_ticket_sales: any = '';
    is_sold_out:boolean = false;
    is_vat_on_commission:boolean = false;
    max_product_sales:number = 0;
    max_ticket_sales:number = 0;
    presale_mode_enabled:boolean = false;
    product_types: any[] = [];
    reps_commission: any = '';
    sell_from: string = '';
    sell_until: string = '';
    serverTime: moment.Moment|any = null;
    status: string = ''; 
    ticket_type_groups: any = [];
    ticket_types: any = [];
    ticket_sell_from: string = '';
    total_product_admin_fee: number = 0;
    total_product_admin_fee_absorbed: number = 0;
    total_product_discounted: number = 0;
    total_product_discounted_value: number = 0;
    total_product_dmn_commission: number = 0;
    total_product_dmn_commission_vat_amount: number = 0;
    total_product_dmn_revenue: number = 0;
    total_product_profit_value: number = 0;
    total_product_rep_commission: number = 0;
    total_product_rep_sales: number = 0;
    total_product_sales: number = 0;
    total_product_sales_value: number = 0;
    total_profit_value: number = 0;
    total_revenue: number = 0;
    total_ticket_admin_fee: number = 0;
    total_ticket_admin_fee_absorbed: number = 0;
    total_ticket_attendees: number = 0;
    total_ticket_attendees_and_reservations: number = 0;
    total_ticket_checkout_reservations: number = 0;
    total_ticket_discounted: number = 0;
    total_ticket_discounted_value: number = 0;
    total_ticket_dmn_commission: number = 0;
    total_ticket_dmn_commission_vat_amount: number = 0;
    total_ticket_dmn_revenue: number = 0;
    total_ticket_guests: number = 0;
    total_ticket_profit_value: number = 0;
    total_ticket_rep_commission: number = 0;
    total_ticket_rep_sales: number = 0;
    total_ticket_reservations: number = 0;
    total_ticket_sales: number = 0;
    total_ticket_sales_value: number = 0;
    total_ticket_tonic_reservations: number = 0;
    total_tickets_ever_available: number = 0;

    itemIds: string = '';
    occurrenceMoment: moment.Moment;

    constructor(itemIds: string, dateString: string, occurrenceData: any = {}) {
        // get each key
        for(let key in occurrenceData) {
            if (this.hasOwnProperty(key)) {
                (<any>this)[key] = occurrenceData[key];
            }
        }

        this.itemIds = itemIds;
        this.occurrenceMoment = moment(dateString, 'YYYYMMDD');
    }

    get ticketTypes() {
        return this.ticket_types;
    }

    /**
     * @desc filters out tickets that are not sellable
     * @param {TicketType}
     * @param {moment.Moment}
     * @return {boolean}
     */
    canSellTypeNow(itemType:TicketType, atTime: moment.Moment) : boolean {
        let sellFrom = itemType.sell_from ? moment(itemType.sell_from) : false;
        let sellUntil = itemType.sell_until ? moment(itemType.sell_until) : false;

        let canSellFrom = !sellFrom || !sellFrom.isValid() || sellFrom.isSameOrBefore(atTime);
        let canSellUntil = !sellUntil || !sellUntil.isValid() || sellUntil.isAfter(atTime);

        return canSellFrom && canSellUntil;
    }

    /**
     * @desc checks if the occurrence has tickets that will come on sale in the future
     * @return {Boolean}
     */
    hasFutureSellableTickets () {
        for(let key in this.ticket_types) {
          if(this.ticket_types[key].status !== 'private' && !this.ticket_types[key].deleted && this.ticket_types[key].applicable_for_date && moment(this.ticket_types[key].sell_from) > moment(this.serverTime)) {
            this.ticket_sell_from = this.ticket_types[key].sell_from;
    
            return true;
          }
        }
    
        return false;
    }

    /**
     * @desc filters out tickets that are not sellable
     * @return {Array<TicketType}
     */
    filterSellableTypes() : Array<TicketType> {
        let sellableTypes: any = {};

        for(let ticketId in this.ticketTypes) {
            let isPublic = this.ticketTypes[ticketId].status !== 'private';

            if(isPublic && !this.ticketTypes[ticketId].deleted && this.ticketTypes[ticketId].applicable_for_date && this.canSellTypeNow(this.ticketTypes[ticketId], this.serverTime)){
                sellableTypes[ticketId] = this.ticketTypes[ticketId];
            }
        }

        return sellableTypes;
    }

    /**
     * @desc returns the ticket type with the specified ID
     * @return {Object}
     */
    getTicketType(ticketId: any) : TicketType|null {
        return this.ticket_types[ticketId] || null;
    }

    /**
     * @desc returns all sellableTicketTypes
     * @return {Object}
     */
    getSellableTicketTypes() {
        return !this.cancelled && this.status !== 'private' ? this.filterSellableTypes() : [];
    }

    /**
     * @desc message to display when no tickets are available
     * @return {string}
     */
    showMessage(timezone: string) : string {
        if(this.cancelled) {
            return 'This event has been cancelled';
        }
        else if(this.status === 'private') {
            return 'This event is not currently on sale';
        }
        else if(!this.presale_mode_enabled && this.hasFutureSellableTickets()) {
            let sellFrom = moment.tz(this.ticket_sell_from, 'Europe/London');
    
            return 'Tickets on sale from ' + sellFrom.tz(timezone).format("dddd, MMMM Do YYYY, h:mm a") + '.';
        }
        else if(this.presale_mode_enabled) {
            return 'Tickets will be on general sale soon.';
        }
        else {
            return 'Sorry, there are no tickets available.';
        }
    }

    /**
     * @desc is there max sales set on the event
     * @return {boolean}
     */
    hasMaxSalesLimit() : boolean {
        return !!this.event_max_ticket_sales;
    }

    /**
     * @desc checks the max sales of the group
     * @param {string}
     * @return {boolean}
     */
    hasGroupMaxSalesLimit(ticketGroupId: any) : boolean {
        let hasLimit = false;

        if(this.ticket_type_groups[ticketGroupId]) {
            hasLimit = typeof this.ticket_type_groups[ticketGroupId].max_ticket_sales === 'number';
        }

        return hasLimit;
    }

    /**
    * @desc
    * @param {any} ticketGroupId
    */
    getTicketTypeIdsInGroup(ticketGroupId: any) : any[] {
        return this.ticket_type_groups[ticketGroupId] ? this.ticket_type_groups[ticketGroupId].ticket_types : [];
    }
}

export default Occurrence;